/**
 * Button
 *
 * Keyboard: Tab focus · Space/Enter activate
 * A11y: aria-busy when loading, aria-disabled when disabled, iconStart/iconEnd aria-hidden
 * Tokens: --nx-accent, --nx-accent-soft, --nx-surface-2, --nx-text, --nx-line, --nx-r-2
 */
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "./utils/cn";
import { Spinner } from "./spinner";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2",
    "rounded-[10px] font-medium select-none whitespace-nowrap",
    "transition-[transform,background,border-color,color,box-shadow] duration-[180ms]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nx-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--nx-bg)]",
    "disabled:pointer-events-none disabled:opacity-50",
    "active:translate-y-[0.5px]",
  ].join(" "),
  {
    variants: {
      variant: {
        primary:
          "bg-[var(--nx-accent)] text-white hover:brightness-110 shadow-[0_0_40px_rgba(107,79,230,0.25)]",
        secondary:
          "bg-[var(--nx-surface-2)] text-[var(--nx-text)] border border-[var(--nx-line)] hover:border-[var(--nx-line-strong)]",
        ghost:
          "text-[var(--nx-text)] hover:bg-[var(--nx-surface-2)]",
        danger:
          "bg-[var(--nx-danger)] text-white hover:brightness-110",
        link:
          "text-[var(--nx-accent-2)] hover:underline underline-offset-4",
      },
      size: {
        sm: "h-8 px-3 text-[0.875rem]",
        // md bumped from h-10 (40px) to h-11 (44px) for WCAG 2.5.5
        // mobile tap-target threshold. Phase 14 Loop 5 mobile audit.
        md: "h-11 px-4 text-[1rem]",
        lg: "h-12 px-6 text-[1.125rem]",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, iconStart, iconEnd, asChild, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    // Radix Slot requires exactly one React child. When asChild is true we
    // forward straight to `children` and ignore iconStart/iconEnd/loading
    // — the caller is expected to compose the icon inside their child.
    // Without this, any Button asChild + iconStart combination crashed the
    // tree with "React.Children.only expected to receive a single React
    // element child." (hotfix 2026-04-21, found via portal-nav-escape
    // Bug 1 test).
    const inner = asChild ? (
      children
    ) : (
      <>
        {loading ? <Spinner size="sm" /> : iconStart ? <span aria-hidden>{iconStart}</span> : null}
        {children}
        {iconEnd && !loading ? <span aria-hidden>{iconEnd}</span> : null}
      </>
    );
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        aria-busy={!asChild && loading ? true : undefined}
        aria-disabled={disabled || undefined}
        disabled={asChild ? undefined : disabled || loading}
        {...props}
      >
        {inner}
      </Comp>
    );
  }
);
Button.displayName = "Button";
